/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import remove from 'lodash.remove';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { PhraseListModalContainer } from 'src/luis/modules/features/components/PhraseListModalContainer';
import { usePhraseLists } from 'src/luis/modules/features/hooks/usePhraseLists';
import { Feature } from 'src/luis/modules/features/types/Feature';
import { PhraseList } from 'src/luis/modules/features/types/PhraseList';
import { conversationFeaturesCommonMessages } from 'src/luis/modules/features/utils/conversationFeaturesCommonMessages';
import { Intent } from 'src/luis/modules/intents/Intent';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { ConversationFeaturePicker } from 'src/luis/verticals/conversationFeaturePicker/ConversationFeaturePicker';
import { useAvailableFeatures } from 'src/luis/verticals/conversationFeaturePicker/useAvailableFeatures';

const getSimpleEntityFeatures = (model: SimpleEntity) => {
    const features = model.features.slice();
    if (model.instanceOf) {
        const { id, name } = model.instanceOf;
        features.push({ id, isRequired: true, typeId: 'entity', name });
    }

    features.sort((lhs, rhs) => (lhs.isRequired === rhs.isRequired ? lhs.name.localeCompare(rhs.name) : lhs.isRequired ? -1 : 1));

    return features;
};

const canHaveRequiredFeatures = (model: Intent | SimpleEntity | CompositeEntity) => model.typeId === ModelType.Simple && !(model as SimpleEntity).children.length;

export type ConversationFeaturePickerContainerProps = {
    /**
     * The entity / intent that uses the features.
     */
    model: Intent | SimpleEntity | CompositeEntity;

    simpleEntityRootId?: LuisId;

    telemetryContext?: string;
};

/**
 * Represents a container component for the feature picker component.
 *
 * @param props The input properties for the container.
 */
export const ConversationFeaturePickerContainer = observer((props: ConversationFeaturePickerContainerProps) => {
    const { model, telemetryContext, simpleEntityRootId } = props;
    const { localize } = useLocalization();
    const availableFeatures = useAvailableFeatures(model).filter(f => f.id !== simpleEntityRootId);
    const { dispatcher, rootStore } = useGlobalState();
    const { phraseLists } = usePhraseLists();
    const [isPhraseListCreationModalHidden, setIsPhraseListCreationModalHidden] = React.useState(true);
    const defaultPhraseListData = { isGlobal: false };
    const [phraseListModalInitData, setPhraseListModalInitData] = React.useState<Partial<PhraseList>>(defaultPhraseListData);
    const [inProgressFeatureIds, setInProgressFeatureIds] = React.useState<LuisId[]>([]);

    const loggerRef = React.useRef(getLogger('ConversationFeaturePickerContainer'));

    const { userRole, displayedUserRole } = rootStore.serviceKey;

    const existingFeatures = (model.typeId === ModelType.Simple ? getSimpleEntityFeatures(model as SimpleEntity) : model.features) || [];

    const genericTracker = useProgressTracker({
        onComplete: (featureId: LuisId) => {
            remove(inProgressFeatureIds, id => id === featureId);
            setInProgressFeatureIds(inProgressFeatureIds);
        }
    });

    /**
     * Adds a new feature to the current model.
     */
    const onAddFeature = (feature: Feature) => {
        loggerRef.current.logEvent(`AddFeatureFrom${telemetryContext}`, { modelName: model.name, feature: feature.name });
        dispatcher.dispatch('addConversationFeature', {
            feature,
            model,
            rootEntityId: simpleEntityRootId,
            toastData: {
                running: { title: localize(conversationFeaturesCommonMessages.addingFeatureRunningTitle) },
                completed: { title: localize(conversationFeaturesCommonMessages.addingFeatureSuccessTitle) },
                failed: { title: localize(conversationFeaturesCommonMessages.addingFeatureFailureTitle) }
            }
        });
    };

    /**
     * Removes the given feature from the current model.
     */
    const onRemoveFeature = (feature: Feature) => {
        setInProgressFeatureIds([...inProgressFeatureIds, feature.id]);
        dispatcher.dispatch('deleteConversationFeature', {
            feature,
            model,
            rootEntityId: simpleEntityRootId,
            tracker: genericTracker,
            toastData: {
                running: { title: localize(conversationFeaturesCommonMessages.removingFeatureRunningTitle) },
                completed: { title: localize(conversationFeaturesCommonMessages.removingFeatureSuccessTitle) },
                failed: { title: localize(conversationFeaturesCommonMessages.removingFeatureFailureTitle) }
            }
        });
    };

    /**
     * Opens the phrase list modal for edit if the clicked feature is a phrase list.
     */
    const onFeatureClick = (feature: Feature) => {
        if (feature.typeId === 'feature') {
            const phraseList = phraseLists.find(p => p.id === feature.id);
            if (!phraseList) {
                return;
            }
            setPhraseListModalInitData(phraseList);
            setIsPhraseListCreationModalHidden(false);
        }
    };

    /**
     * Callback function that Adds new phrase list as feature to the current model.
     * Note: If the phrase list marked as global so it'll be already added as a feature
     * for the all models through the phrase list modal container.
     *
     * @param phraseList The phraseList to be added as feature.
     */
    const onSubmitNewPhraseList = (phraseList: PhraseList) => {
        const isUpdate = phraseListModalInitData.id && phraseList.id === phraseListModalInitData.id;
        if (!phraseList.isGlobal && !isUpdate) {
            onAddFeature({ name: phraseList.name, id: phraseList.id, typeId: 'feature', isRequired: false });
        }
        setIsPhraseListCreationModalHidden(true);
        setPhraseListModalInitData(defaultPhraseListData);
    };

    const toggleRequire = (feature: Feature, required: boolean) => {
        // When a feature isRequired is toggled, every other feature should be NOT required
        const features = model.features.map(f => ({ ...f, isRequired: f.id === feature.id ? required : false }));

        setInProgressFeatureIds([...inProgressFeatureIds, feature.id]);
        dispatcher.dispatch('toggleConversationFeatureRequired', {
            model: model as SimpleEntity,
            rootEntityId: simpleEntityRootId,
            features,
            tracker: genericTracker,
            featureId: feature.id,
            isRequired: required
        });
    };

    return (
        <>
            <ConversationFeaturePicker
                availableFeatures={availableFeatures}
                existingFeatures={existingFeatures}
                onAddFeature={onAddFeature}
                onCreateNewPhraseList={name => {
                    setIsPhraseListCreationModalHidden(false);
                    setPhraseListModalInitData({ name });
                }}
                onRemoveFeature={onRemoveFeature}
                onFeatureClick={onFeatureClick}
                disabledFeatureIds={phraseLists.filter(p => !p.isActive).map(p => p.id)}
                inProgressFeatureIds={inProgressFeatureIds}
                requiredFeatureId={existingFeatures.find(f => f.isRequired)?.id}
                onToggleRequire={canHaveRequiredFeatures(model) ? toggleRequire : null}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
            />

            <PhraseListModalContainer
                hidden={isPhraseListCreationModalHidden}
                initData={{ ...defaultPhraseListData, ...phraseListModalInitData }}
                onSubmit={onSubmitNewPhraseList}
                onDismiss={() => {
                    setIsPhraseListCreationModalHidden(true);
                    setPhraseListModalInitData(defaultPhraseListData);
                }}
            />
        </>
    );
});
